/**
 * Keeps a render error inside the part of the screen that threw it.
 *
 * Without a boundary, React unmounts the **whole** tree on an error thrown
 * while rendering: one broken panel blanks the shell, the menu and every other
 * panel with it. Wrap a part whose failure should not cost the rest of the
 * screen — a module's dashboard panel, a module's page.
 *
 * It catches render and lifecycle errors only. A failed request is not one:
 * that is `useAsync`'s error and `ErrorState`'s to show. React logs what it
 * catches to the console, so nothing is lost by rendering nothing.
 *
 * A class, because React still has no hook for this.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * What renders instead of `children` after an error. A function gets the
   * error and a `reset` that tries `children` again. Omit it to render nothing
   * — right for a panel that may as well not be there.
   */
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
  /**
   * When any of these change, a caught error is dropped and `children` render
   * again: a new route, another record. Compared one by one with `Object.is`.
   */
  resetKeys?: readonly unknown[];
  /** Told about each error caught. React has already logged it. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

function changed(a: readonly unknown[] = [], b: readonly unknown[] = []): boolean {
  return a.length !== b.length || a.some((value, index) => !Object.is(value, b[index]));
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(thrown: unknown): ErrorBoundaryState {
    return { error: thrown instanceof Error ? thrown : new Error(String(thrown)) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  override componentDidUpdate(previous: ErrorBoundaryProps): void {
    if (this.state.error && changed(previous.resetKeys, this.props.resetKeys)) this.reset();
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    const { fallback } = this.props;
    if (typeof fallback === 'function') return fallback(error, this.reset);
    return fallback ?? null;
  }
}
